import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  RotateCcw,
  Settings,
} from 'lucide-react'
import Hls from 'hls.js'

interface VideoPlayerProps {
  src: string
  poster?: string
  title?: string
  autoPlay?: boolean
  onEnded?: () => void
}

function formatTime(seconds: number) {
  if (!isFinite(seconds)) return '0:00'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2]

export default function VideoPlayer({ src, poster, title, autoPlay, onEnded }: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [buffered, setBuffered] = useState(0)
  const [volume, setVolume] = useState(1)
  const [muted, setMuted] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [showSpeed, setShowSpeed] = useState(false)
  const [speed, setSpeed] = useState(1)
  const [ended, setEnded] = useState(false)
  const [seeking, setSeeking] = useState(false)
  const [currentQuality, setCurrentQuality] = useState(-1)
  const [qualityLevels, setQualityLevels] = useState<{ height: number; index: number }[]>([])
  const [showQuality, setShowQuality] = useState(false)

  const v = videoRef.current
  const isHLS = src.includes('.m3u8')

  // Initialize HLS or native video source
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }
    setQualityLevels([])
    setCurrentQuality(-1)

    if (isHLS && Hls.isSupported()) {
      const hls = new Hls()
      hls.loadSource(src)
      hls.attachMedia(video)

      hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
        const levels = data.levels.map((level, index) => ({
          height: level.height,
          index,
        }))
        setQualityLevels(levels)
        setCurrentQuality(-1) // auto
      })

      hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
        if (hlsRef.current?.autoLevelEnabled) {
          setCurrentQuality(-1)
        } else {
          setCurrentQuality(data.level)
        }
      })

      hlsRef.current = hls
    } else if (isHLS && video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = src
    } else {
      video.src = src
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
    }
  }, [src, isHLS])

  // Auto-hide controls
  const resetHideTimer = useCallback(() => {
    setShowControls(true)
    clearTimeout(hideTimer.current)
    if (playing) {
      hideTimer.current = setTimeout(() => setShowControls(false), 3000)
    }
  }, [playing])

  useEffect(() => {
    resetHideTimer()
    return () => clearTimeout(hideTimer.current)
  }, [playing, resetHideTimer])

  // Sync fullscreen state
  useEffect(() => {
    const onFsChange = () => setFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!containerRef.current?.contains(document.activeElement) && document.activeElement !== document.body) return
      const vid = videoRef.current
      if (!vid) return

      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault()
          vid.paused ? vid.play() : vid.pause()
          break
        case 'ArrowLeft':
          e.preventDefault()
          vid.currentTime = Math.max(0, vid.currentTime - 5)
          break
        case 'ArrowRight':
          e.preventDefault()
          vid.currentTime = Math.min(vid.duration, vid.currentTime + 5)
          break
        case 'ArrowUp':
          e.preventDefault()
          vid.volume = Math.min(1, vid.volume + 0.1)
          setVolume(vid.volume)
          break
        case 'ArrowDown':
          e.preventDefault()
          vid.volume = Math.max(0, vid.volume - 0.1)
          setVolume(vid.volume)
          break
        case 'f':
          e.preventDefault()
          toggleFullscreen()
          break
        case 'm':
          e.preventDefault()
          vid.muted = !vid.muted
          setMuted(vid.muted)
          break
      }
      resetHideTimer()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [resetHideTimer])

  const togglePlay = () => {
    if (!v) return
    if (ended) {
      v.currentTime = 0
      setEnded(false)
    }
    v.paused ? v.play() : v.pause()
  }

  const toggleFullscreen = () => {
    if (!containerRef.current) return
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      containerRef.current.requestFullscreen()
    }
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!v || !progressRef.current) return
    const rect = progressRef.current.getBoundingClientRect()
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    v.currentTime = pct * v.duration
  }

  const handleProgressDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!seeking) return
    handleProgressClick(e)
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value)
    setVolume(val)
    if (v) {
      v.volume = val
      v.muted = val === 0
      setMuted(val === 0)
    }
  }

  const toggleMute = () => {
    if (!v) return
    v.muted = !v.muted
    setMuted(v.muted)
  }

  const changeSpeed = (s: number) => {
    setSpeed(s)
    if (v) v.playbackRate = s
    setShowSpeed(false)
  }

  const changeQuality = (level: number) => {
    if (!hlsRef.current) return
    if (level === -1) {
      hlsRef.current.currentLevel = -1
      setCurrentQuality(-1)
    } else {
      hlsRef.current.currentLevel = level
      setCurrentQuality(level)
    }
    setShowQuality(false)
  }

  const getQualityLabel = () => {
    if (currentQuality === -1) return 'Авто'
    const level = qualityLevels.find(l => l.index === currentQuality)
    return level ? `${level.height}p` : 'Авто'
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0
  const bufferedPct = duration > 0 ? (buffered / duration) * 100 : 0

  return (
    <div
      ref={containerRef}
      className="relative aspect-video bg-black rounded-2xl overflow-hidden group select-none"
      onMouseMove={resetHideTimer}
      onMouseLeave={() => playing && setShowControls(false)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('[data-controls]')) return
        togglePlay()
        resetHideTimer()
      }}
      onContextMenu={(e) => e.preventDefault()}
      tabIndex={0}
    >
      <video
        ref={videoRef}
        poster={poster}
        className="w-full h-full object-contain"
        playsInline
        autoPlay={autoPlay}
        preload="metadata"
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onPlay={() => { setPlaying(true); setEnded(false) }}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setEnded(true); onEnded?.() }}
        onTimeUpdate={() => v && setCurrentTime(v.currentTime)}
        onDurationChange={() => v && setDuration(v.duration)}
        onProgress={() => {
          if (!v || v.buffered.length === 0) return
          setBuffered(v.buffered.end(v.buffered.length - 1))
        }}
      />

      {/* Center play/replay overlay */}
      {(!playing || ended) && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            {ended ? (
              <RotateCcw className="size-9 text-white" />
            ) : (
              <Play className="size-9 text-white ml-1" />
            )}
          </div>
        </div>
      )}

      {/* Gradient overlay for controls */}
      <div
        className={`absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/80 to-transparent transition-opacity duration-300 pointer-events-none ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Title overlay */}
      {title && (
        <div
          className={`absolute top-0 inset-x-0 p-4 bg-gradient-to-b from-black/60 to-transparent transition-opacity duration-300 pointer-events-none ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <p className="text-white text-sm font-medium truncate">{title}</p>
        </div>
      )}

      {/* Controls bar */}
      <div
        data-controls
        className={`absolute inset-x-0 bottom-0 px-4 pb-3 pt-8 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Progress bar */}
        <div
          ref={progressRef}
          className="relative h-1 bg-white/30 rounded-full mb-3 cursor-pointer group/progress hover:h-1.5 transition-all"
          onClick={handleProgressClick}
          onMouseDown={() => setSeeking(true)}
          onMouseMove={handleProgressDrag}
          onMouseUp={() => setSeeking(false)}
          onMouseLeave={() => setSeeking(false)}
        >
          {/* Buffered */}
          <div
            className="absolute inset-y-0 left-0 bg-white/30 rounded-full"
            style={{ width: `${bufferedPct}%` }}
          />
          {/* Played */}
          <div
            className="absolute inset-y-0 left-0 bg-primary-500 rounded-full"
            style={{ width: `${progress}%` }}
          />
          {/* Thumb */}
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 size-3 bg-primary-500 rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity"
            style={{ left: `${progress}%` }}
          />
        </div>

        {/* Control buttons */}
        <div className="flex items-center gap-3">
          {/* Play/Pause */}
          <button onClick={togglePlay} className="text-white hover:text-primary-300 transition cursor-pointer">
            {ended ? (
              <RotateCcw className="size-5" />
            ) : playing ? (
              <Pause className="size-5" />
            ) : (
              <Play className="size-5 ml-0.5" />
            )}
          </button>

          {/* Time */}
          <span className="text-white text-xs font-mono tabular-nums">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <div className="flex-1" />

          {/* Quality selector (HLS only) */}
          {qualityLevels.length > 0 && (
            <div className="relative">
              <button
                onClick={() => { setShowQuality(!showQuality); setShowSpeed(false) }}
                className="text-white hover:text-primary-300 transition cursor-pointer text-xs font-medium bg-white/20 px-2 py-0.5 rounded"
              >
                {getQualityLabel()}
              </button>
              {showQuality && (
                <div className="absolute bottom-8 right-0 bg-gray-900/95 backdrop-blur-sm rounded-xl py-2 min-w-[100px] z-20">
                  <button
                    onClick={() => changeQuality(-1)}
                    className={`w-full text-left px-4 py-1.5 text-sm transition cursor-pointer ${
                      currentQuality === -1 ? 'text-primary-400 font-semibold' : 'text-white hover:text-primary-300'
                    }`}
                  >
                    Авто
                  </button>
                  {qualityLevels
                    .sort((a, b) => b.height - a.height)
                    .map((level) => (
                      <button
                        key={level.index}
                        onClick={() => changeQuality(level.index)}
                        className={`w-full text-left px-4 py-1.5 text-sm transition cursor-pointer ${
                          currentQuality === level.index ? 'text-primary-400 font-semibold' : 'text-white hover:text-primary-300'
                        }`}
                      >
                        {level.height}p
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* Speed */}
          <div className="relative">
            <button
              onClick={() => { setShowSpeed(!showSpeed); setShowQuality(false) }}
              className="text-white hover:text-primary-300 transition cursor-pointer flex items-center gap-1"
            >
              <Settings className="size-4" />
              {speed !== 1 && <span className="text-xs">{speed}x</span>}
            </button>
            {showSpeed && (
              <div className="absolute bottom-8 right-0 bg-gray-900/95 backdrop-blur-sm rounded-xl py-2 min-w-[100px] z-20">
                {SPEEDS.map((s) => (
                  <button
                    key={s}
                    onClick={() => changeSpeed(s)}
                    className={`w-full text-left px-4 py-1.5 text-sm transition cursor-pointer ${
                      speed === s ? 'text-primary-400 font-semibold' : 'text-white hover:text-primary-300'
                    }`}
                  >
                    {s}x
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Volume */}
          <div className="flex items-center gap-1.5 group/vol">
            <button onClick={toggleMute} className="text-white hover:text-blue-300 transition cursor-pointer">
              {muted || volume === 0 ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={handleVolumeChange}
              className="vol-slider w-0 group-hover/vol:w-12 transition-all duration-200 cursor-pointer h-0.5"
            />
          </div>


          {/* Fullscreen */}
          <button onClick={toggleFullscreen} className="text-white hover:text-primary-300 transition cursor-pointer">
            {fullscreen ? <Minimize className="size-5" /> : <Maximize className="size-5" />}
          </button>
        </div>
      </div>
    </div>
  )
}
